import { useState, useEffect, useMemo, forwardRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Briefcase,
  Users,
  Clock,
  FileCheck,
  Receipt,
  HardHat,
  GraduationCap,
  Calendar,
  Building,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Icon mapping for employment types
const TYPE_ICONS = {
  paye_employee: Users,
  paye_part_time: Clock,
  ir35_inside: FileCheck,
  ir35_outside: Receipt,
  contractor: Briefcase,
  cis_subcontractor: HardHat,
  apprentice: GraduationCap,
  fixed_term: Calendar,
  agency: Building
};

/**
 * Employment Type Badge - displays an employment type as a colored badge
 */
export const EmploymentTypeBadge = ({ type, size = 'default', showIcon = true, className }) => {
  if (!type) return null;

  const Icon = TYPE_ICONS[type.code] || Briefcase;
  const sizeClasses = {
    sm: 'text-[10px] px-1.5 py-0.5',
    default: 'text-xs px-2 py-1',
    lg: 'text-sm px-3 py-1.5'
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 font-medium rounded-full border",
        sizeClasses[size],
        className
      )}
      style={{
        backgroundColor: `${type.color}15`,
        borderColor: `${type.color}40`,
        color: type.color
      }}
    >
      {showIcon && <Icon className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />}
      {type.name}
    </span>
  );
};

/**
 * Employment Type Selector - dropdown for selecting employment type
 */
export const EmploymentTypeSelector = forwardRef(({
  value,
  onChange,
  companyId,
  disabled = false,
  placeholder = 'Select employment type',
  showInactive = false,
  className,
  triggerClassName
}, ref) => {
  const { userProfile } = useAuth();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const effectiveCompanyId = companyId || userProfile?.company_id;

  // Fetch employment types
  useEffect(() => {
    const fetchTypes = async () => {
      if (!effectiveCompanyId) return;

      try {
        setLoading(true);
        let query = supabase
          .from('employment_types')
          .select('*')
          .eq('company_id', effectiveCompanyId)
          .order('display_order', { ascending: true });

        if (!showInactive) {
          query = query.eq('is_active', true);
        }

        const { data, error } = await query;

        if (error) throw error;
        setTypes(data || []);
      } catch (err) {
        console.error('Error fetching employment types:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTypes();
  }, [effectiveCompanyId, showInactive]);

  // Find the selected type
  const selectedType = useMemo(() => {
    return types.find(t => t.id === value);
  }, [types, value]);

  if (loading) {
    return (
      <div className={cn("flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 bg-slate-50", className)}>
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-sm text-slate-400">Loading...</span>
      </div>
    );
  }

  return (
    <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger ref={ref} className={cn("rounded-lg", triggerClassName, className)}>
        <SelectValue placeholder={placeholder}>
          {selectedType ? (
            <div className="flex items-center gap-2">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: selectedType.color }}
              />
              <span>{selectedType.name}</span>
            </div>
          ) : (
            placeholder
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {types.map((type) => {
          const Icon = TYPE_ICONS[type.code] || Briefcase;
          return (
            <SelectItem key={type.id} value={type.id}>
              <div className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: type.color }}
                />
                <Icon className="h-4 w-4" style={{ color: type.color }} />
                <span>{type.name}</span>
                {!type.is_active && (
                  <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">Inactive</Badge>
                )}
              </div>
            </SelectItem>
          );
        })}
        {types.length === 0 && (
          <div className="px-2 py-4 text-center text-sm text-slate-500">
            No employment types available
          </div>
        )}
      </SelectContent>
    </Select>
  );
});

EmploymentTypeSelector.displayName = 'EmploymentTypeSelector';

/**
 * Hook to fetch employment types for a company
 */
export const useEmploymentTypes = (companyId, showInactive = false) => {
  const { userProfile } = useAuth();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const effectiveCompanyId = companyId || userProfile?.company_id;

  useEffect(() => {
    const fetchTypes = async () => {
      if (!effectiveCompanyId) return;

      try {
        setLoading(true);
        setError(null);

        let query = supabase
          .from('employment_types')
          .select('*')
          .eq('company_id', effectiveCompanyId)
          .order('display_order', { ascending: true });

        if (!showInactive) {
          query = query.eq('is_active', true);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;
        setTypes(data || []);
      } catch (err) {
        console.error('Error fetching employment types:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTypes();
  }, [effectiveCompanyId, showInactive]);

  // Helper to get type by ID
  const getTypeById = (id) => types.find(t => t.id === id);

  // Helper to get type by code
  const getTypeByCode = (code) => types.find(t => t.code === code);

  // Types grouped by entitlement status
  const typesWithLeave = useMemo(() => types.filter(t => t.has_leave_entitlement), [types]);
  const typesWithoutLeave = useMemo(() => types.filter(t => !t.has_leave_entitlement), [types]);

  return {
    types,
    loading,
    error,
    getTypeById,
    getTypeByCode,
    typesWithLeave,
    typesWithoutLeave
  };
};

export default EmploymentTypeSelector;
